import { useCallback, useEffect, useRef } from 'react'

/**
 * Shared press-tail for the hold-to-record surfaces (VoiceMicButton,
 * VoiceRecorder, PushToTalkMic). The press STARTS on the consumer's
 * element; the tail (move / up / cancel) is owned by window-level
 * capture-phase listeners keyed to the press's pointerId.
 *
 * Element-scoped tails are not enough: iOS Safari drops pointer capture
 * when the DOM changes under a touch — a strip mounting over the button,
 * the held button relabeling itself, a sibling mounting mid-press — and
 * the release then lands somewhere handler-less while the recorder runs
 * on (Mira's iPhone, 2026-07-24; fixed for VoiceMicButton in PR #244,
 * extracted here for its siblings). Window delivery survives overlay
 * mounts, relabels, display:none ancestors, and capture drops alike.
 *
 * Contract:
 *   - `begin(e)` in the consumer's onPointerDown: captures the pointer
 *     (best-effort) and attaches the tail for THAT pointerId only — a
 *     second finger's up/cancel can't end the gesture. Any stale tail
 *     is detached first; one live press at most.
 *   - `onRelease` / `onCancel` fire at most once per press; the hook
 *     detaches the listeners and releases capture BEFORE invoking them,
 *     so starting a new press from inside a callback is safe.
 *   - `onMove` keeps firing until the press ends or the consumer calls
 *     `end()` (e.g. a drag threshold handing the gesture to tap
 *     controls).
 *   - `end()` detaches manually (lock paths, disabled-mid-press
 *     effects); unmount detaches automatically. Callbacks are read
 *     through latest refs — pass fresh closures every render, nothing
 *     re-attaches.
 */

export type PressTailCallbacks = {
  onMove?: (ev: PointerEvent) => void
  onRelease: (ev: PointerEvent) => void
  onCancel: (ev: PointerEvent) => void
}

export function usePressTail(callbacks: PressTailCallbacks) {
  // Latest-ref mirror: the window listeners attach once per press, so
  // reading callbacks through their closure would go stale by release
  // time.
  const callbacksRef = useRef(callbacks)
  useEffect(() => {
    callbacksRef.current = callbacks
  })

  // Active press: the capturing element + pointerId (for
  // releasePointerCapture) and the window-listener detach function.
  const pressRef = useRef<{
    el: Element
    pointerId: number
    detach: () => void
  } | null>(null)

  const end = useCallback(() => {
    const press = pressRef.current
    if (!press) return
    pressRef.current = null
    press.detach()
    try {
      press.el.releasePointerCapture(press.pointerId)
    } catch {
      // best-effort — capture may already be gone
    }
  }, [])

  const begin = useCallback(
    (e: { currentTarget: Element; pointerId: number }) => {
      end()
      const el = e.currentTarget
      const pointerId = e.pointerId
      try {
        el.setPointerCapture(pointerId)
      } catch {
        // best-effort — capture can fail on synthetic pointers
      }
      const move = (ev: PointerEvent) => {
        if (ev.pointerId === pointerId) callbacksRef.current.onMove?.(ev)
      }
      const up = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return
        end()
        callbacksRef.current.onRelease(ev)
      }
      const cancel = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return
        end()
        callbacksRef.current.onCancel(ev)
      }
      // No move listener for consumers without drag gestures — pointermove
      // fires at pointer rate for the whole press. (Removal of a
      // never-added listener in detach is a spec'd no-op.)
      if (callbacksRef.current.onMove) window.addEventListener('pointermove', move, true)
      window.addEventListener('pointerup', up, true)
      window.addEventListener('pointercancel', cancel, true)
      pressRef.current = {
        el,
        pointerId,
        detach: () => {
          window.removeEventListener('pointermove', move, true)
          window.removeEventListener('pointerup', up, true)
          window.removeEventListener('pointercancel', cancel, true)
        },
      }
    },
    [end],
  )

  // Unmount mid-press (SPA nav): the window listeners are ours to remove.
  useEffect(() => end, [end])

  return { begin, end }
}
